'use strict';

Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });

const react = require('@emotion/react');
const styleEncapsulation = require('@zapier/style-encapsulation');
const React = require('react');
const theme_Colors = require('../../../theme/Colors.cjs.js');
const theme_Shadows = require('../../../theme/Shadows.cjs.js');
require('../../../theme/Typography.cjs.js');
const theme_LegacyTypography = require('../../../theme/LegacyTypography.cjs.js');
const theme_LegacyColorMap = require('../../../theme/LegacyColorMap.cjs.js');
const components_display_Heading_Heading = require('../../display/Heading/Heading.cjs.js');
const components_display_Heading_HeadingLevel = require('../../display/Heading/HeadingLevel.cjs.js');
const components_display_Icon_Icon = require('../../display/Icon/Icon.cjs.js');
const components_navigation_Button_Button = require('../../navigation/Button/Button.cjs.js');
const components_navigation_Link_Link = require('../../navigation/Link/Link.cjs.js');
const styleMixins_reset = require('../../../style-mixins/reset.cjs.js');

const breakpoints = {
  tablet: 768,
  desktop: 1230
};
const ContentStyles = {
  iconContainer: react.css`
    ${styleMixins_reset.boxSizing};
    margin-bottom: 15px;
  `,
  mediaContainer: react.css`
    ${styleMixins_reset.boxSizing};
    width: 100%;
    margin-bottom: 20px;
  `,
  heading: [
    react.css`
      ${styleMixins_reset.boxSizing};
      margin-bottom: 10px;
      color: ${theme_Colors.Colors.GrayWarm10};
      ${theme_LegacyTypography.LegacyTypography.pageHeader9}
    `
  ],
  description: [
    react.css`
      ${styleMixins_reset.boxSizing};
      margin-bottom: 10px;
      color: ${theme_Colors.Colors.GrayWarm10};
      ${theme_LegacyTypography.LegacyTypography.paragraph3}

      @media screen and (min-width: ${breakpoints.tablet}px) {
        ${theme_LegacyTypography.LegacyTypography.paragraph1Short}
      }
    `
  ],
  smallText: [
    react.css`
      ${styleMixins_reset.boxSizing};
      margin-top: 0;
      margin-bottom: 10px;
      color: ${theme_Colors.Colors.GrayWarm9};
      ${theme_LegacyTypography.LegacyTypography.smallPrint1}

      @media screen and (min-width: ${breakpoints.tablet}px) {
        ${theme_LegacyTypography.LegacyTypography.paragraph3}
      }
    `
  ]
};
const PrimaryCTAStyles = {
  primaryCtaContainer: react.css`
    & > a,
    & > button {
      width: 100%;
    }

    @media screen and (min-width: ${breakpoints.tablet}px) {
      & > a,
      & > button {
        width: auto;
      }
    }
  `
};
const SecondaryCTAStyles = {
  secondaryCtaContainer: react.css`
    ${styleMixins_reset.boxSizing};
    margin-top: 10px;

    & > a {
      display: inline-flex;
      align-items: center;
    }
  `,
  secondaryCtaText: react.css`
    ${styleMixins_reset.boxSizing};
    ${theme_LegacyTypography.LegacyTypography.paragraph3Bold}
  `,
  secondaryCtaIconContainer: react.css`
    ${styleMixins_reset.boxSizing};
    margin-left: 6px;
    height: 24px;
  `
};
const Styles = styleEncapsulation.labelStyles("Block", Object.assign(Object.assign(Object.assign({ container: (isCentered) => [
  react.css`
      background-color: transparent;
      display: flex;
      flex-direction: column;
      margin: 0 auto;
      align-items: ${isCentered ? "center" : "flex-start"};
      text-align: ${isCentered ? "center" : "left"};
    `
], link: react.css`
    ${styleMixins_reset.interactiveReset};
    &:hover img {
      box-shadow: ${theme_Shadows.Shadows.elevation20};
    }
  ` }, ContentStyles), PrimaryCTAStyles), SecondaryCTAStyles));
const Content = ({ iconName, iconColor, media, heading, description, smallText }) => {
  return react.jsx(
    React.Fragment,
    null,
    iconName && react.jsx(
      "div",
      { "data-testid": "icon", css: Styles.iconContainer, "data-zds": true },
      react.jsx(components_display_Icon_Icon.Icon, { color: iconColor ? theme_LegacyColorMap.LegacyColorMap[iconColor] : void 0, name: iconName, size: 50 })
    ),
    media && react.jsx("div", { css: Styles.mediaContainer, "data-zds": true }, media),
    heading && react.jsx(
      components_display_Heading_HeadingLevel.HeadingLevel,
      null,
      react.jsx(components_display_Heading_Heading.Heading, { css: Styles.heading }, heading)
    ),
    description && react.jsx("div", { css: Styles.description, "data-zds": true }, description),
    smallText && react.jsx("p", { css: Styles.smallText, "data-zds": true }, smallText)
  );
};
const PrimaryCTA = ({ ctaHref, onCtaClick, onCtaKeyDown, ctaText }) => react.jsx(
  "div",
  { css: Styles.primaryCtaContainer, "data-zds": true },
  react.jsx(components_navigation_Button_Button.Button, { color: "primary", href: ctaHref, onClick: onCtaClick, onKeyDown: onCtaKeyDown, "data-testid": "cta-button" }, ctaText)
);
const SecondaryCTA = ({ secondaryCtaHref, onSecondaryCtaClick, onSecondaryCtaKeyDown, secondaryCtaText }) => {
  return react.jsx(
    "div",
    { css: Styles.secondaryCtaContainer, "data-zds": true },
    react.jsx(
      components_navigation_Link_Link.Link,
      { "data-testid": "cta-secondary-button", href: secondaryCtaHref, onClick: onSecondaryCtaClick, onKeyDown: onSecondaryCtaKeyDown, weight: "bold" },
      react.jsx("div", { css: Styles.secondaryCtaText, "data-zds": true }, secondaryCtaText)
    )
  );
};
function Block({ isLink = false, isCentered = false, description, iconName, iconColor = "blue", media, ctaText, ctaHref, secondaryCtaText, secondaryCtaHref, heading, smallText, onCtaClick, onCtaKeyDown, onSecondaryCtaClick, onSecondaryCtaKeyDown }) {
  const hasPrimaryCta = Boolean(ctaText && (ctaHref || onCtaClick));
  const hasSecondaryCta = Boolean(secondaryCtaText && (secondaryCtaHref || onSecondaryCtaClick));
  const contentProps = {
    iconName,
    iconColor,
    media,
    heading,
    description,
    smallText
  };
  const primaryCTAProps = {
    ctaHref,
    onCtaClick,
    onCtaKeyDown,
    ctaText
  };
  const secondaryCTAProps = {
    secondaryCtaHref,
    onSecondaryCtaClick,
    onSecondaryCtaKeyDown,
    secondaryCtaText
  };
  return react.jsx("div", { css: Styles.container(isCentered), "data-zds": true }, isLink ? react.jsx(
    components_navigation_Link_Link.Link,
    { href: ctaHref, "data-testid": "link-wrapper", onClick: onCtaClick, onKeyDown: onCtaKeyDown, css: Styles.link },
    react.jsx(Content, Object.assign({}, contentProps))
  ) : react.jsx(
    React.Fragment,
    null,
    react.jsx(Content, Object.assign({}, contentProps)),
    hasPrimaryCta && react.jsx(PrimaryCTA, Object.assign({}, primaryCTAProps)),
    hasSecondaryCta && react.jsx(SecondaryCTA, Object.assign({}, secondaryCTAProps))
  ));
}

exports.Block = Block;
//# sourceMappingURL=Block.cjs.js.map
